import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Logo } from '@barev/ui';
import { useTRPC } from '@barev/web';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { BO_LOCALE_LABELS, BO_LOCALES, useT } from '../i18n.tsx';
import { I } from '../components/kit.tsx';

/** Вход: ссылка в рабочий бот → бот подтверждает → страница опрашивает и получает сессию */
export function Login() {
  const { t, locale, setLocale } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const start = useMutation(trpc.bo.auth.start.mutationOptions());
  const poll = useMutation(trpc.bo.auth.poll.mutationOptions());
  const devUsers = useQuery(trpc.bo.auth.devUsers.queryOptions());
  const devLogin = useMutation(trpc.bo.auth.devLogin.mutationOptions({ onSuccess: () => qc.invalidateQueries() }));
  const [qr, setQr] = useState<string>();
  const [expired, setExpired] = useState(false);

  const req = start.data;
  useEffect(() => {
    start.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!req) return;
    setExpired(false);
    QRCode.toDataURL(req.url, { margin: 0, width: 312, color: { dark: '#4a1028', light: '#ffffff' } }).then(setQr, () => setQr(undefined));
    const timer = setInterval(async () => {
      try {
        const r = await poll.mutateAsync({ code: req.code, pollKey: req.pollKey });
        if (r.status === 'ok') {
          clearInterval(timer);
          await qc.invalidateQueries();
        } else if (r.status === 'expired') {
          clearInterval(timer);
          setExpired(true);
        }
      } catch {
        /* сеть моргнула — следующий опрос */
      }
    }, 2000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [req?.code]);

  return (
    <div className="bo-login">
      <section className="bo-login__cover">
        <Logo size={24} color="#f6e9dc" />
        <p className="bo-login__word">
          Barev,
          <br />
          <i>{t('login.coverWord')}</i>
        </p>
        <div className="bo-langs" role="group" aria-label="Language">
          {BO_LOCALES.map((l) => (
            <button key={l} type="button" className="bo-lang" aria-pressed={l === locale} onClick={() => setLocale(l)}>
              {BO_LOCALE_LABELS[l]}
            </button>
          ))}
        </div>
      </section>

      <section className="bo-login__form">
        <h1 className="bo-title">{t('login.title')}</h1>
        <p className="bo-sub" style={{ marginTop: 0 }}>
          {t('login.lead')}
        </p>

        {expired ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
            <span className="bo-muted">{t('login.expired')}</span>
            <button type="button" className="bo-btn" onClick={() => start.mutate()}>
              {t('login.retry')}
            </button>
          </div>
        ) : (
          <>
            <a className="bo-btn bo-btn--accent" style={{ alignSelf: 'flex-start', height: 50, padding: '0 24px' }} href={req?.url} target="_blank" rel="noreferrer" aria-disabled={!req}>
              <I name="telegram" size={18} />
              {t('login.open')}
            </a>
            {qr && (
              <div className="bo-qr-wrap" style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
                <img className="bo-qr" src={qr} alt="" />
                <span className="bo-muted bo-small" style={{ maxWidth: 200 }}>
                  {t('login.qr')}
                </span>
              </div>
            )}
            {req && (
              <span className="bo-wait" role="status">
                <span className="bo-pulse" />
                {t('login.waiting')}
              </span>
            )}
          </>
        )}

        <p className="bo-faint bo-small" style={{ margin: 0 }}>
          {t('login.noTelegram')}
        </p>

        {!!devUsers.data?.length && (
          <div className="bo-dev">
            <div>
              <div className="bo-h3">{t('login.dev')}</div>
              <div className="bo-faint bo-small">{t('login.devHint')}</div>
            </div>
            <div className="bo-pills">
              {devUsers.data.map((u) => (
                <button key={u.id} type="button" className="bo-btn bo-btn--ghost bo-btn--sm" onClick={() => devLogin.mutate({ id: u.id })}>
                  {u.name} · {u.roles.map((r) => t(`role.${r}`)).join(', ')}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
